import React, { useState } from 'react';
import { Plus, Send } from 'lucide-react';
import { CostLineItem, COST_CATEGORY_LABELS, DEFAULT_TAX_RATE } from '../../types/billing';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { CostLineItemRow } from './CostLineItemRow';
import { CostSummaryCard } from './CostSummaryCard';

interface QuotationFormProps {
  workOrderId: string;
  vendorId: string;
  onSubmit: (items: CostLineItem[], validityDays: number, notes: string) => void;
  onCancel: () => void;
  isSaving?: boolean;
}

export const QuotationForm: React.FC<QuotationFormProps> = ({
  workOrderId,
  vendorId,
  onSubmit,
  onCancel,
  isSaving = false,
}) => {
  const [items, setItems] = useState<CostLineItem[]>([]);
  const [validityDays, setValidityDays] = useState(7);
  const [notes, setNotes] = useState('');

  const [newDesc, setNewDesc] = useState('');
  const [newCategory, setNewCategory] = useState<CostLineItem['category']>('material');
  const [newQty, setNewQty] = useState(1);
  const [newUnit, setNewUnit] = useState('pcs');
  const [newRate, setNewRate] = useState(0);

  const handleAddItem = () => {
    if (!newDesc.trim() || newRate <= 0) return;
    const newItem: CostLineItem = {
      item_id: `QIT-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      description: newDesc.trim(),
      quantity: newQty,
      unit: newUnit,
      unit_price: newRate,
      amount: newQty * newRate,
      category: newCategory,
    };
    setItems([...items, newItem]);
    setNewDesc('');
    setNewQty(1);
    setNewRate(0);
  };

  const handleRemoveItem = (itemId: string) => {
    setItems(items.filter(i => i.item_id !== itemId));
  };

  const handleSubmit = () => {
    if (items.length === 0) return;
    onSubmit(items, validityDays, notes);
  };

  const subtotal = items.reduce((s, i) => s + i.amount, 0);

  return (
    <div className="space-y-4">
      <Card title="Submit Quotation" subtitle="Add line items and submit for approval">
        <div className="space-y-4">
          <div className="space-y-2">
            {items.map((item) => (
              <CostLineItemRow key={item.item_id} item={item} onRemove={handleRemoveItem} />
            ))}
            {items.length === 0 && (
              <p className="text-xs text-slate-500 text-center py-4">No items added yet.</p>
            )}
          </div>

          <div className="flex flex-wrap items-end gap-2 p-3 bg-slate-950 rounded-lg border border-slate-800">
            <div className="flex-1 min-w-[140px]">
              <label className="text-[10px] text-slate-500 mb-1 block">Description</label>
              <input
                type="text"
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
                placeholder="Item description"
                className="w-full px-2 py-1.5 bg-slate-800 border border-slate-700 rounded text-xs text-slate-200 focus:outline-none focus:border-sky-500"
              />
            </div>
            <div className="w-28">
              <label className="text-[10px] text-slate-500 mb-1 block">Category</label>
              <select
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value as CostLineItem['category'])}
                className="w-full px-2 py-1.5 bg-slate-800 border border-slate-700 rounded text-xs text-slate-200 focus:outline-none focus:border-sky-500"
              >
                {Object.entries(COST_CATEGORY_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div className="w-14">
              <label className="text-[10px] text-slate-500 mb-1 block">Qty</label>
              <input
                type="number"
                min={1}
                value={newQty}
                onChange={(e) => setNewQty(parseInt(e.target.value) || 1)}
                className="w-full px-2 py-1.5 bg-slate-800 border border-slate-700 rounded text-xs text-slate-200 focus:outline-none focus:border-sky-500"
              />
            </div>
            <div className="w-16">
              <label className="text-[10px] text-slate-500 mb-1 block">Unit</label>
              <select
                value={newUnit}
                onChange={(e) => setNewUnit(e.target.value)}
                className="w-full px-2 py-1.5 bg-slate-800 border border-slate-700 rounded text-xs text-slate-200 focus:outline-none focus:border-sky-500"
              >
                <option value="pcs">pcs</option>
                <option value="hrs">hrs</option>
                <option value="kg">kg</option>
                <option value="mtr">mtr</option>
                <option value="lot">lot</option>
              </select>
            </div>
            <div className="w-24">
              <label className="text-[10px] text-slate-500 mb-1 block">Rate (Rs)</label>
              <input
                type="number"
                min={0}
                value={newRate || ''}
                onChange={(e) => setNewRate(parseFloat(e.target.value) || 0)}
                className="w-full px-2 py-1.5 bg-slate-800 border border-slate-700 rounded text-xs text-slate-200 focus:outline-none focus:border-sky-500"
              />
            </div>
            <Button variant="secondary" size="sm" onClick={handleAddItem} icon={<Plus className="w-3.5 h-3.5" />} disabled={!newDesc.trim() || newRate <= 0}>
              Add
            </Button>
          </div>

          {items.length > 0 && (
            <CostSummaryCard materials={items.filter(i => i.category === 'material')} labor={items.filter(i => i.category === 'labor')} service={items.filter(i => i.category === 'service')} other={items.filter(i => i.category === 'other')} taxRate={DEFAULT_TAX_RATE} />
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] text-slate-500 mb-1 block">Validity (Days)</label>
              <input
                type="number"
                min={1}
                value={validityDays}
                onChange={(e) => setValidityDays(parseInt(e.target.value) || 7)}
                className="w-full px-2 py-1.5 bg-slate-800 border border-slate-700 rounded text-xs text-slate-200 focus:outline-none focus:border-sky-500"
              />
            </div>
            <div>
              <label className="text-[10px] text-slate-500 mb-1 block">Notes</label>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional notes"
                className="w-full px-2 py-1.5 bg-slate-800 border border-slate-700 rounded text-xs text-slate-200 focus:outline-none focus:border-sky-500"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onCancel}>Cancel</Button>
            <Button variant="primary" onClick={handleSubmit} isLoading={isSaving} disabled={items.length === 0} icon={<Send className="w-4 h-4" />}>
              Submit Quotation
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
